// src/pages/CookiePolicy.jsx
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";

function slugify(str = "") {
  return String(str)
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

export default function CookiePolicy() {
  const { t } = useTranslation();
  const sections = t("legal.cookies.sections", { returnObjects: true }) || [];
  const list = Array.isArray(sections) ? sections : [];
  const lastUpdated = t("legal.last_updated");

  const ui = t("legal.ui", { returnObjects: true }) || {};

  const toc = useMemo(
    () => list.map((s) => ({ text: s?.h ?? "", id: slugify(s?.h ?? "") })),
    [list]
  );

  return (
    <>

      {/* Color header band */}
      <section className="border-b border-slate-200 bg-gradient-to-r from-indigo-50 via-violet-50 to-fuchsia-50">
        <div className="mx-auto max-w-7xl px-4 py-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-xs font-medium text-violet-700 ring-1 ring-violet-200">
            <span>{ui.badge}</span>
            <span className="text-slate-400">•</span>
            <span>{t("legal.cookies.title")}</span>
          </div>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
            {t("legal.cookies.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-600">{lastUpdated}</p>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* TOC */}
        <aside className="hidden lg:block lg:col-span-3">
          <div className="sticky top-24 rounded-2xl border border-violet-100 bg-white p-4 shadow-[0_1px_0_0_rgba(139,92,246,.15)]">
            <p className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-3">
              {ui.contents}
            </p>
            <nav className="space-y-1.5">
              {toc.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="block rounded-lg px-2 py-1.5 text-sm text-slate-700 hover:text-violet-700 hover:bg-violet-50"
                >
                  {item.text}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <div className="lg:col-span-9 space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            {list.map((s, idx) => {
              const id = slugify(s?.h ?? `section-${idx}`);
              return (
                <section key={id} id={id} className={idx ? "mt-8" : ""}>
                  <h3 className="text-xl font-semibold mb-2 text-slate-900">
                    {s?.h}
                  </h3>
                  <div
                    className="prose prose-slate max-w-none prose-a:text-violet-700 hover:prose-a:underline"
                    dangerouslySetInnerHTML={{ __html: s?.p || "" }}
                  />
                </section>
              );
            })}
          </div>
        </div>
      </main>
    </>
  );
}
